import { z } from 'zod';
import { ZodProperties } from '../zod';
import { useMemo } from 'react';

// enum values are short to reduce space in the URL
export enum SearchParamsDataSharingEnum {
  NamedMeasure = 'nm',
  NamedFilter = 'nf',
  VirtualEvent = 've',
  Dictionary = 'dict',
  DashboardSchedule = 'ds',
}

interface ISearchParamsDataSharingBase {
  type: SearchParamsDataSharingEnum;
}

interface INamedMeasureSearchParamsData extends ISearchParamsDataSharingBase {
  type: SearchParamsDataSharingEnum.NamedMeasure;
  schemaId: string;
  namedMeasureId: string;
}

interface INamedFilterSearchParamsData extends ISearchParamsDataSharingBase {
  type: SearchParamsDataSharingEnum.NamedFilter;
  schemaId: string;
  namedFilterId: string;
}

interface IVirtualEventSearchParamsData extends ISearchParamsDataSharingBase {
  type: SearchParamsDataSharingEnum.VirtualEvent;
  schemaId: string;
  virtualEventId: string;
}

interface IDictionarySearchParamsData extends ISearchParamsDataSharingBase {
  type: SearchParamsDataSharingEnum.Dictionary;
  /**
   * if schemaId is empty, we use the default dictionary schema
   * this is because BucketDef doesn't have schema bind to it
   */
  schemaId?: string;
  term: string;
}

interface IDashboardScheduleSearchParamsData
  extends ISearchParamsDataSharingBase {
  type: SearchParamsDataSharingEnum.DashboardSchedule;
  schemaId: string;
  term: string;
}

type ISearchParamsDataSharingArgs =
  | INamedMeasureSearchParamsData
  | INamedFilterSearchParamsData
  | IVirtualEventSearchParamsData
  | IDictionarySearchParamsData
  | IDashboardScheduleSearchParamsData;

const namedMeasureSchema = z.object<
  ZodProperties<INamedMeasureSearchParamsData>
>({
  type: z.literal(SearchParamsDataSharingEnum.NamedMeasure),
  schemaId: z.string(),
  namedMeasureId: z.string(),
});

const namedFilterSchema = z.object<ZodProperties<INamedFilterSearchParamsData>>(
  {
    type: z.literal(SearchParamsDataSharingEnum.NamedFilter),
    schemaId: z.string(),
    namedFilterId: z.string(),
  },
);

const virtualEventSchema = z.object<
  ZodProperties<IVirtualEventSearchParamsData>
>({
  type: z.literal(SearchParamsDataSharingEnum.VirtualEvent),
  schemaId: z.string(),
  virtualEventId: z.string(),
});

const dictionarySchema = z.object<ZodProperties<IDictionarySearchParamsData>>({
  type: z.literal(SearchParamsDataSharingEnum.Dictionary),
  schemaId: z.string(),
  term: z.string(),
});

const dashboardScheduleSchema = z.object<
  ZodProperties<IDashboardScheduleSearchParamsData>
>({
  type: z.literal(SearchParamsDataSharingEnum.DashboardSchedule),
  schemaId: z.string(),
  term: z.string(),
});

const searchParamsDataSharingSchema = z.discriminatedUnion('type', [
  namedMeasureSchema,
  namedFilterSchema,
  virtualEventSchema,
  dictionarySchema,
  dashboardScheduleSchema,
]);

// Validates arguments and generates url with search params
export const getUrlForDataSharing = (
  pathname: string,
  args: ISearchParamsDataSharingArgs,
): string | null => {
  const result = searchParamsDataSharingSchema.safeParse(args);

  if (!result.success) {
    return null;
  }

  const { data } = result;

  const searchParams = new URLSearchParams(Object.entries(data));

  if (pathname.includes('?')) {
    return `${pathname}&${searchParams.toString()}`;
  }

  return `${pathname}?${searchParams.toString()}`;
};

// Validates search params and gets data from it
export const useDataFromSearchParams = (
  searchParams: URLSearchParams,
): ISearchParamsDataSharingArgs | null => {
  return useMemo(() => {
    const result = searchParamsDataSharingSchema.safeParse(
      Object.fromEntries(searchParams.entries()),
    );

    if (!result.success) {
      return null;
    }

    return result.data;
  }, [searchParams]);
};
